import { useField, useFormikContext } from 'formik';

import {
  APISource,
  source_allowed_http_methods,
  SourceAllowedHTTPMethod,
} from '../../../../../../../../typings/Source.interface';
import { fieldName } from '../../../../../utils';
import Button, { ClickableArea } from '../../../../common/base/Button';
import Icon from '../../../../common/base/Icon';
import Text from '../../../../common/base/Text';
import resource_details_form_props from './resource_details';
import { Div } from '../../../../common/helpers/StyledUtils';
import { cleanseFormErrorObject } from '../../../../../utils/form';
import { SelectSourceType } from '../../GuidedConnection/Steps/SelectSourceType';
import styled from 'styled-components';
import Alert from '../../../../common/base/Alert';
import { useContext, useEffect, useState } from 'react';
import {
  IntegrationConfig,
  isSourceTypeFeatureUnmanaged,
  SourceAuthProvider,
  SourceType,
  SourceTypes,
} from '../../../../../../../../typings/Integration.interface';
import { DashboardContext } from '../../DashboardContext';
import field_formats from '../../../../../utils/field-formatters';
import {
  CustomResponseValue,
  SourceFeature,
  SourceFeatureType,
  useFeatures,
} from './form_features';
import Link from '../../../../common/base/Link';

type ErrorOfFormValue<T> = {
  [P in keyof T]?: T[P] extends object ? ErrorOfFormValue<T[P]> : string;
};

// Derived so a method added to the canonical list cannot be missed here. The form
// round-trips this map key by key, so a missing key silently strips that method from a
// source on every edit — which is exactly what happened to `HEAD`.
type AllowedHTTPMethodConfig = Record<SourceAllowedHTTPMethod, boolean>;

/** The methods `SourceModel` applies when a source specifies none. */
const default_methods: readonly SourceAllowedHTTPMethod[] = ['POST', 'PUT', 'PATCH', 'DELETE'];

const buildAllowedHTTPMethodConfig = (
  is_allowed: (method: SourceAllowedHTTPMethod) => boolean,
): AllowedHTTPMethodConfig =>
  source_allowed_http_methods.reduce(
    (acc, method) => ({ ...acc, [method]: is_allowed(method) }),
    {} as AllowedHTTPMethodConfig,
  );

export interface SourceConfigurationFormValues {
  show_advanced: boolean;
  enable_auth: boolean;
  enable_custom_response: boolean;
  type?: SourceType;
  config?: IntegrationConfig & {
    allowed_http_methods?: AllowedHTTPMethodConfig;
    custom_response?: CustomResponseValue;
    trim_payload?: boolean;
    auth_type?: SourceAuthProvider;
    auth?: IntegrationConfig;
  };
}

const default_allowed_http_methods = buildAllowedHTTPMethodConfig((method) =>
  default_methods.includes(method),
);

const StyledHR = styled.hr`
  height: 1px;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.outline.neutral};
  border: none;
`;

function parseAllowedHTTPMethods(allowed_http_methods?: AllowedHTTPMethodConfig) {
  return Object.entries(allowed_http_methods || {})
    .filter(([, value]) => !!value)
    .map(([key]) => key);
}

const source_configuration_form_props = {
  // Needs the type's schema, because `auth_type` is only sent when the type
  // declares it as one of its auth methods. The argument is required so a
  // caller cannot forget it; the value may still be undefined while the
  // dashboard context loads.
  postprocessValues: (
    values: SourceConfigurationFormValues,
    source_types: SourceTypes | undefined,
  ) => {
    const verification = values.type
      ? source_types?.[values.type]?.features?.VERIFICATION
      : undefined;
    const declares_auth_type =
      !!values.config?.auth_type &&
      !!verification &&
      isSourceTypeFeatureUnmanaged(verification) &&
      !!verification.auth?.[values.config.auth_type];

    return {
      type: values.type,
      config: {
        allowed_http_methods: parseAllowedHTTPMethods(values.config?.allowed_http_methods),
        custom_response: values.enable_custom_response ? values.config?.custom_response : null,
        // Only types declaring the TRIM_PAYLOAD feature accept this key —
        // never send it for other types (their config schema strips it).
        ...(values.type && source_types?.[values.type]?.features?.TRIM_PAYLOAD
          ? { trim_payload: !!values.config?.trim_payload }
          : {}),
        // Types without auth methods report the source type as their auth_type,
        // and their config schema has no such field, so it is dropped. A value
        // the type actually declares as a method is always sent, even when the
        // method is named after the type itself.
        auth_type:
          values.enable_auth && (declares_auth_type || values.type !== values.config?.auth_type)
            ? values.config?.auth_type
            : null,
        auth: values.enable_auth ? values.config?.auth || {} : null,
      },
    };
  },
  getInitialValues: (
    source?: APISource,
    options?: { in_guided_connection?: boolean },
  ): SourceConfigurationFormValues => {
    const default_custom_response = {
      content_type: 'json',
      body: '',
    } as const;

    return source
      ? {
          type: source.type,
          show_advanced: !options?.in_guided_connection,
          enable_custom_response: !!source.config.custom_response,
          enable_auth: source.authenticated,
          config: {
            ...source.config,
            trim_payload: source.config.trim_payload ?? false,
            custom_response:
              'custom_response' in source.config
                ? source.config.custom_response || default_custom_response
                : default_custom_response,
            allowed_http_methods:
              'allowed_http_methods' in source.config
                ? buildAllowedHTTPMethodConfig(
                    (method) => source.config.allowed_http_methods?.includes(method) ?? false,
                  )
                : default_allowed_http_methods,
            auth_type: source.config.auth_type,
            auth: source.config?.auth,
          },
        }
      : {
          enable_custom_response: false,
          enable_auth: false,
          show_advanced: false,
          config: {
            custom_response: default_custom_response,
            allowed_http_methods: default_allowed_http_methods,
            trim_payload: false,
          },
        };
  },
  validate: async (values: SourceConfigurationFormValues, source_types?: SourceTypes) => {
    const errors: ErrorOfFormValue<SourceConfigurationFormValues> = {
      config: {} as any,
    };

    if (!values.type) {
      errors.type = 'Required';
    }

    if (values.enable_auth && values.type) {
      // TODO: this lookup appears to resolve to undefined, so the loop below
      // validates nothing. Every other read of a type's features in this file
      // goes through `?.features?.VERIFICATION`; this one has no `.features`
      // in the path. Predates the auth-method picker — confirm before changing,
      // since fixing it turns on required-field validation that has been
      // dormant.
      const source_type_fields = [
        ...(source_types?.[values.type.toUpperCase()]?.VERIFICATION?.fields || []),
      ];
      source_type_fields
        .filter((field) => field.features?.includes('VERIFICATION'))
        .forEach((field) => {
          if (field.required && !values.config?.[field.name]) {
            (errors.config as any)[field.name] = 'Required';
          }
        });

      // A type offering a choice of auth method keeps that method's fields under
      // `config.auth`, not at the top of `config`, so the loop above never sees
      // them. Require whatever the selected method declares, and nest the errors
      // under `config.auth` to match the paths those inputs render at.
      const verification = source_types?.[values.type]?.features?.VERIFICATION;
      const selected_auth =
        verification && isSourceTypeFeatureUnmanaged(verification) && values.config?.auth_type
          ? verification.auth?.[values.config.auth_type]
          : undefined;
      selected_auth?.fields
        ?.filter((field) => field.required !== false)
        .forEach((field) => {
          const auth = values.config?.auth as Record<string, string> | undefined;
          if (!auth?.[field.name]) {
            (errors.config as any).auth = {
              ...((errors.config as any).auth || {}),
              [field.name]: 'Required',
            };
          }
        });
    }

    if (values.config?.allowed_http_methods) {
      if (Object.values(values.config.allowed_http_methods).every((value) => !value)) {
        (errors.config as any).allowed_http_methods = 'At least one HTTP method must be selected';
      }
    }

    if (values.enable_custom_response) {
      if (values.config?.custom_response?.content_type === 'json') {
        try {
          JSON.parse(values.config?.custom_response?.body);
        } catch (e) {
          (errors.config as any).custom_response = {
            body: 'Invalid JSON',
          };
        }
      }
    }

    return cleanseFormErrorObject(errors);
  },
  Fields: ({
    prefix,
    show_all,
    disable_name,
    hide_optional_features,
    in_guided_connection = false,
    advanced_configuration_display = 'accordion',
  }: {
    prefix: string;
    show_all?: boolean;
    disable_name?: boolean;
    hide_optional_features?: boolean;
    in_guided_connection?: boolean;
    advanced_configuration_display?: 'accordion' | 'section';
  }) => {
    const formikContext = useFormikContext();
    const { source_types } = useContext(DashboardContext);
    const [using_default_name, setUsingDefaultName] = useState(false);
    const [{ value: name }, , { setValue: setName }] = useField<string>(fieldName('name', prefix));
    const [{ value: show_advanced }, , { setValue: setShowAdvanced }] = useField<boolean>(
      fieldName('show_advanced', prefix),
    );
    const [{ value: type }, , { setTouched: setTypeTouched }] = useField<SourceType>(
      fieldName('type', prefix),
    );
    const [{ value: enable_auth }, , { setValue: setEnableVerification }] = useField<boolean>(
      fieldName('enable_auth', prefix),
    );
    const [, , { setValue: setAuthType }] = useField<SourceType>(
      fieldName('config.auth_type', prefix),
    );
    const [{ value: search_value }] = useField(`search.${fieldName('type', prefix)}`);

    const selected_type = source_types?.[type];
    const configuration_alert = selected_type?.configuration_alert;
    const configuration_link = selected_type?.configuration_link;
    const configuration_link_text = selected_type?.configuration_link_text;
    const { required_features, advanced_features } = useFeatures(
      selected_type?.features || {},
      undefined,
      hide_optional_features,
      in_guided_connection,
    );

    const onSelectSourceType = (source_type: SourceType) => {
      const selected_type = source_types?.[source_type];
      setTypeTouched(false);
      // Auth config carried over from the previously selected type is invalid
      // for the new type and gets rejected by the API ("config.auth is not
      // allowed"): a required-verification type sets enable_auth and writes
      // config.auth fields that nothing clears on type change. Reset auth
      // here; the required-verification effect below re-enables it when the
      // new type requires it. The legacy-auth "Migrate to X" link bypasses
      // this on purpose to carry the auth config over to the dedicated type.
      setEnableVerification(false);
      formikContext.setFieldValue(fieldName('config.auth_type', prefix), undefined);
      formikContext.setFieldValue(fieldName('config.auth', prefix), null);
      // Seed the method picker from the new type's declared default (Baselinker only
      // ever sends HEAD, for instance). Unlike a `managed` feature this sets the
      // initial selection and nothing more: the checkboxes stay editable and the
      // default is not re-applied when the Source is later updated.
      const method_feature = selected_type?.features?.CUSTOM_HTTP_METHOD;
      if (
        method_feature &&
        isSourceTypeFeatureUnmanaged(method_feature) &&
        method_feature.default_value
      ) {
        const seeded_methods = method_feature.default_value;
        formikContext.setFieldValue(
          fieldName('config.allowed_http_methods', prefix),
          source_allowed_http_methods.reduce(
            (acc, method) => ({ ...acc, [method]: seeded_methods.includes(method) }),
            {} as AllowedHTTPMethodConfig,
          ),
        );
      }
      if (selected_type?.category === 'platform') {
        setName(field_formats.slugify(selected_type?.label?.toLocaleLowerCase() || ''));
        setUsingDefaultName(true);
      } else {
        if (using_default_name || !!search_value?.length) {
          const name = field_formats.slugify(search_value?.toLocaleLowerCase() || '');
          setName(name);
          setUsingDefaultName(false);
        }
      }
    };

    useEffect(() => {
      const verification = selected_type?.features.VERIFICATION;
      if (verification && isSourceTypeFeatureUnmanaged(verification) && verification.required) {
        setEnableVerification(true);
      }
    }, [selected_type, setEnableVerification]);

    useEffect(() => {
      if (!enable_auth) {
        formikContext.setFieldValue('config.auth', null);
      }
    }, [enable_auth, setAuthType, type]);

    const matching_source_type =
      selected_type?.category === 'http' && name
        ? Object.entries(source_types || {}).find(([, source_type]) => {
            const words = name.toLowerCase().split(/[-_\s]+/);
            return (
              words.includes(source_type.label.toLowerCase()) && source_type.category !== 'http'
            );
          })
        : undefined;

    const show_name_alert = !!matching_source_type;
    const show_advanced_as_section = advanced_configuration_display === 'section';

    return (
      <>
        <SelectSourceType prefix={prefix} onSelect={onSelectSourceType} />
        {(show_all || !!type) && (
          <>
            {configuration_alert && (
              <Alert inline info m={{ y: 4 }}>
                {configuration_alert}
                {configuration_link && configuration_link_text && (
                  <>
                    {' '}
                    <Link icon={'link'} href={configuration_link}>
                      {configuration_link_text}
                    </Link>
                  </>
                )}
              </Alert>
            )}
            {disable_name !== true && (
              <Div m={{ y: 4 }}>
                <resource_details_form_props.Fields
                  prefix={prefix}
                  key={selected_type?.label}
                  placeholder="shopify-prod"
                  name_prefix="Source"
                  help="Used to identify your source while using Hookdeck."
                  name_required
                  default_value={
                    selected_type?.category === 'platform'
                      ? selected_type?.label?.toLocaleLowerCase()
                      : undefined
                  }
                />
              </Div>
            )}
            <Div m={{ y: 4 }}>
              {required_features.map((feature) => (
                <SourceFeature
                  feature={feature as SourceFeatureType}
                  prefix={prefix}
                  key={feature.type}
                />
              ))}
            </Div>
            {show_name_alert && matching_source_type && (
              <Div m={{ y: 6 }}>
                <Alert info inline>
                  Hookdeck has a pre-configured source type matching the name you've entered. We
                  recommend matching your type above for ease of use.
                  <Button
                    outline
                    m={{ t: 3 }}
                    onClick={() => {
                      formikContext.setFieldValue(
                        fieldName('type', prefix),
                        matching_source_type[0] as SourceType,
                      );
                      formikContext.setFieldTouched(fieldName('type', prefix), true);
                      onSelectSourceType(matching_source_type[0] as SourceType);
                    }}>
                    Switch to {matching_source_type[1].label} source type
                  </Button>
                </Alert>
              </Div>
            )}
            {advanced_features.length > 0 ? (
              <Div m={{ y: 4, t: 5 }}>
                <Div
                  flex={{
                    justify: 'flex-start',
                    align: 'center',
                    gap: show_advanced_as_section ? 2 : 1,
                  }}>
                  {show_advanced_as_section ? (
                    <Text size="xs" semi muted white_space="nowrap" style={{ flexShrink: 0 }}>
                      Advanced configuration
                    </Text>
                  ) : (
                    <ClickableArea
                      p={{ y: 0, x: 1 }}
                      m={{ l: -1 }}
                      rounded
                      style={{ width: 'auto' }}
                      flex={{ justify: 'flex-start', align: 'center', gap: 1 }}
                      onClick={() => setShowAdvanced(!show_advanced)}>
                      <Text size="xs" semi muted={!show_advanced}>
                        Advanced configuration
                      </Text>
                      <Icon muted icon={show_advanced ? 'arrow_drop_down' : 'arrow_right'} />
                    </ClickableArea>
                  )}
                  {(show_advanced || show_advanced_as_section) && <StyledHR />}
                </Div>
                {(show_advanced || show_advanced_as_section) &&
                  advanced_features.map((feature) => (
                    <SourceFeature
                      feature={feature as SourceFeatureType}
                      prefix={prefix}
                      key={feature.type}
                    />
                  ))}
              </Div>
            ) : null}
          </>
        )}
      </>
    );
  },
};

export default source_configuration_form_props;
